import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Lock, ClipboardCheck, CheckCircle2, XCircle, RotateCcw, Award, ArrowRight } from "lucide-react";
import { authHeaders, formatApiErrorDetail } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Intro({ summary, onStart, loading }) {
  return (
    <div className="bg-white border border-slate-200/60 rounded-2xl p-8 sm:p-10 max-w-3xl mx-auto" data-testid="test-intro">
      <div className="h-14 w-14 rounded-2xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-5"><ClipboardCheck className="h-7 w-7" /></div>
      <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900">Abschlusstest Theorieteil</h1>
      <p className="mt-3 text-slate-600 leading-relaxed">16 Fragen aus allen vier Modulen (je 4 pro Modul). Zu jeder Frage ist genau eine Antwort richtig. Bestanden ab <strong>12 richtigen Antworten (75 %)</strong>. Es gibt kein Zeitlimit; Sie können den Test beliebig oft wiederholen.</p>
      {summary.test_versuche > 0 && (
        <div className={`mt-5 p-4 rounded-xl border text-sm ${summary.test_bestanden ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="test-bisher">
          {summary.test_bestanden ? <><strong>Bereits bestanden</strong> – bestes Ergebnis {summary.bestes_ergebnis}/16 Punkte. Sie können den Test zur Übung erneut absolvieren.</> : <>Bisher {summary.test_versuche} Versuch(e), bestes Ergebnis {summary.bestes_ergebnis}/16. Nicht aufgeben – wiederholen Sie gern die Lektionen und versuchen Sie es erneut.</>}
        </div>
      )}
      <Button onClick={onStart} disabled={loading} className="mt-8 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 px-8 font-semibold" data-testid="test-start-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{summary.test_versuche > 0 ? "Test erneut starten" : "Test starten"}<ArrowRight className="ml-2 h-4 w-4" /></>}</Button>
    </div>
  );
}

function Ergebnis({ result, onRetry }) {
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="test-ergebnis">
      <div className={`rounded-2xl p-8 sm:p-10 text-white ${result.bestanden ? "bg-green-600" : "bg-slate-800"}`}>
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center">{result.bestanden ? <Award className="h-8 w-8" /> : <RotateCcw className="h-8 w-8" />}</div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider opacity-80">{result.bestanden ? "Bestanden" : "Leider nicht bestanden"}</p>
            <p className="font-['Manrope'] text-3xl sm:text-4xl font-bold" data-testid="test-punkte">{result.punkte} / {result.anzahl} Punkte</p>
          </div>
        </div>
        <p className="mt-5 text-white/90 leading-relaxed">{result.bestanden ? "Herzlichen Glückwunsch! Sie haben den Theorieteil erfolgreich abgeschlossen. Die Kursleitung gibt Ihr Zertifikat in Kürze frei – Sie finden es dann in Ihrer Übersicht." : `Für das Bestehen sind ${result.bestehensgrenze} richtige Antworten nötig. Schauen Sie sich die Erklärungen unten an, wiederholen Sie bei Bedarf die Lektionen und starten Sie den Test erneut.`}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {result.bestanden ? <Link to="/schulungen/teilnehmer"><Button className="bg-white text-green-700 hover:bg-green-50 rounded-xl h-11 font-semibold" data-testid="zur-uebersicht-btn">Zur Übersicht</Button></Link> : <Button onClick={onRetry} className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl h-11 font-semibold" data-testid="test-retry-btn"><RotateCcw className="mr-2 h-4 w-4" />Erneut versuchen</Button>}
          {!result.bestanden && <Link to="/schulungen/teilnehmer/modul/m1"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-xl h-11 bg-transparent">Lektionen wiederholen</Button></Link>}
        </div>
      </div>
      <div className="space-y-3">
        <h2 className="font-['Manrope'] text-lg font-bold text-slate-800">Auswertung</h2>
        {result.details.map((d, i) => (
          <div key={d.id} className={`bg-white border rounded-2xl p-5 ${d.korrekt ? "border-green-200" : "border-red-200"}`} data-testid={`auswertung-${d.id}`}>
            <div className="flex gap-3">
              {d.korrekt ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
              <div className="flex-1 text-sm">
                <p className="font-semibold text-slate-800">{i + 1}. {d.frage}</p>
                {!d.korrekt && <p className="mt-2 text-red-700"><span className="font-semibold">Ihre Antwort:</span> {d.gegebene_antwort || "keine Antwort"}</p>}
                <p className={`mt-1 ${d.korrekt ? "text-green-700" : "text-slate-700"}`}><span className="font-semibold">Richtig:</span> {d.richtige_antwort}</p>
                <p className="mt-2 text-slate-500 italic">{d.erklaerung}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TestView() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [fragen, setFragen] = useState(null);
  const [antworten, setAntworten] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = () => axios.get(`${API}/schulungen/kurs`, authHeaders(user)).then((r) => setSummary(r.data.summary)).catch(() => toast.error("Daten konnten nicht geladen werden"));
  useEffect(() => { document.title = "Abschlusstest | Lernbereich"; loadSummary(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/schulungen/kurs/test`, authHeaders(user)); setFragen(data.fragen); setAntworten({}); setResult(null); window.scrollTo(0, 0); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  const submit = async () => {
    const offen = fragen.filter((f) => antworten[f.id] === undefined).length;
    if (offen > 0 && !window.confirm(`${offen} Frage(n) sind noch unbeantwortet. Trotzdem abgeben?`)) return;
    setLoading(true);
    try { const { data } = await axios.post(`${API}/schulungen/kurs/test/abgeben`, { antworten }, authHeaders(user)); setResult(data); setFragen(null); loadSummary(); window.scrollTo(0, 0); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  if (!summary) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;

  if (!summary.test_freigeschaltet) return (
    <div className="bg-white border border-slate-200/60 rounded-2xl p-10 max-w-2xl mx-auto text-center" data-testid="test-locked">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-4"><Lock className="h-7 w-7" /></div>
      <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Abschlusstest noch gesperrt</h1>
      <p className="mt-3 text-slate-600">Sie haben {summary.lektionen_done} von {summary.lektionen_total} Lektionen abgeschlossen. Der Test wird freigeschaltet, sobald alle Lektionen bearbeitet sind.</p>
      <Link to="/schulungen/teilnehmer/modul/m1"><Button className="mt-6 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-11 px-6 font-semibold" data-testid="test-locked-weiterlernen">Weiterlernen</Button></Link>
    </div>
  );

  if (result) return <Ergebnis result={result} onRetry={start} />;
  if (!fragen) return <Intro summary={summary} onStart={start} loading={loading} />;

  const beantwortet = Object.keys(antworten).length;
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="test-fragen">
      <div className="sticky top-[104px] z-10 bg-white/90 backdrop-blur border border-slate-200/60 rounded-2xl px-5 py-3 flex items-center gap-4">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-cyan-600 rounded-full transition-all" style={{ width: `${(beantwortet / fragen.length) * 100}%` }} /></div>
        <span className="text-sm font-semibold text-slate-700" data-testid="test-progress">{beantwortet} / {fragen.length} beantwortet</span>
      </div>
      {fragen.map((f, i) => (
        <fieldset key={f.id} className="bg-white border border-slate-200/60 rounded-2xl p-6" data-testid={`frage-${f.id}`}>
          <legend className="sr-only">Frage {i + 1}</legend>
          <p className="text-xs font-semibold tracking-wider uppercase text-cyan-700">Frage {i + 1} · Modul {f.modul_id.replace("m", "")}</p>
          <p className="mt-2 font-semibold text-slate-800 text-base">{f.frage}</p>
          <div className="mt-4 space-y-2">
            {f.optionen.map((o) => {
              const checked = antworten[f.id] === o.id;
              return (
                <label key={o.id} className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${checked ? "border-cyan-600 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`option-${f.id}-${o.id}`}>
                  <input type="radio" name={f.id} value={o.id} checked={checked} onChange={() => setAntworten((a) => ({ ...a, [f.id]: o.id }))} className="mt-1 h-4 w-4 accent-cyan-700" />
                  <span className="text-sm text-slate-700">{o.text}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div className="flex justify-end pt-2">
        <Button onClick={submit} disabled={loading} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 px-8 font-semibold" data-testid="test-abgeben-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test abgeben"}</Button>
      </div>
    </div>
  );
}
